// 🔬 B242 (AI_111) — CARREGADOR Firestore → RegistroPesquisa[] (puro + testável)
// ─────────────────────────────────────────────────────────────────────────────
// Transforma os arrays já carregados em memória pelos contextos (pacientes,
// exames, sorologias) num registro ACHATADO e PSEUDONIMIZADO por paciente, que é
// o que o motor de coorte conta/calcula.
//
// REGRA DE OURO (B242): identificador NUNCA entra no RegistroPesquisa. O único
// elo com o paciente é `pacienteHash` — o MESMO pseudônimo irreversível usado
// pela IA (`gerarHashPaciente`), garantindo consistência entre análise e pesquisa.
//
// Esta função é PURA (recebe arrays + "hoje" injetável) → coberta por testes; a
// correção do mapeamento é crítica (erro de nome de campo = dado errado na coorte).
import { RegistroPesquisa } from "../types";
import {
  calcularGRWR,
  calcularRemanescente,
  calcularIMC,
} from "../../../shared/utils/volumetriaDoador";
import {
  gerarHashPaciente,
  normalizarDataParaISO,
  normalizarContagemMilhar,
} from "../../ia-analise/services/anonimizarDados";
import {
  normalizarSorologia,
  normalizarAutoanticorpo,
  normalizarCargaViral,
} from "../../sorologias/services/normalizarSorologia";
import { calcularMELD } from "../../../utils/meld";

// ── Entradas mínimas (tipagem estrutural: os tipos do app encaixam aqui) ──────

export interface PacienteEntrada {
  id: string;
  dataNascimento?: string;
  sexo?: string;
  tipoSanguineo?: string;
  tipoAtendimento?: string;
  statusPaciente?: string;
  numeroRGCT?: string;
  rgct?: string;
  dataTransplante?: string;
  situacaoEspecial?: boolean;
  tipoSE?: string;
  comorbidades?: string;
  avaliacaoDoador?: Record<string, any>; // B351 (AI_152)
  avaliacaoClinica?: {
    causaCirrose?: string;
    ascite?: boolean;
    encefalopatia?: boolean;
    // Hipertensão portal / descompensação (B242 MVP3a, AI_112) — booleanos
    // estruturados da aba Avaliação Clínica, agora pesquisáveis.
    varizesEsofago?: boolean;
    varizesGastrica?: boolean;
    hdaPrevia?: boolean;
    pbe?: boolean;
    empiemaBacterianoEspontaneo?: boolean;
    colangiteRepeticao?: boolean;
    colangioTransparietoHepatica?: boolean;
    shr?: boolean;
    hipertensaoPortopulmonar?: boolean;
    shdHepatopulmonar?: boolean;
    pruridoIntratavel?: boolean;
    insuficienciaHepaticaAguda?: boolean;
    aclf?: boolean;
    tips?: boolean;
    dialise?: boolean;
    hidrotorax?: boolean;
    sarcopenia?: boolean;
    paracentese?: boolean;
    ligaduraElastica?: boolean;
    hepatocarcinoma?: boolean;
    hccMilao?: string;
    hccBCLC?: string;
    hccTerapiaLocorregional?: boolean;
    hccTerapiaIntencao?: string;
  };
  inscricaoTx?: {
    meld?: number;
    meldNa?: number;
    childPugh?: string | number;
    numeroRGCT?: string;
    dataTransplante?: string;
    situacaoEspecial?: boolean;
    tipoSE?: string;
  };
  scores?: { meld?: number; meldNa?: number; childPugh?: string | number };
}

export interface ExameEntrada {
  pacienteId: string;
  data?: string;
  tipoExame?: "pre" | "pos";
  meld?: number;
  childPugh?: string;
  hemoglobina?: number;
  plaquetas?: number;
  creatinina?: number;
  bilirrubinaTotal?: number;
  albumina?: number;
  inr?: number;
  sodio?: number;
  tacrolimus?: number;
  nivelTacrolimus?: number;
  // Laboratório ampliado (B242 MVP3a, AI_112) — todos numéricos (números ou
  // strings numéricas; numOuNull tolera ambos).
  tgo?: number | string;
  tgp?: number | string;
  ggt?: number | string;
  fosfataseAlcalina?: number | string;
  bilirrubinaDireta?: number | string;
  leucocitos?: number | string;
  potassio?: number | string;
  calcio?: number | string;
  ureia?: number | string;
  glicemia?: number | string;
  hemoglobinaGlicada?: number | string;
  pcr?: number | string;
  amilase?: number | string;
  lipase?: number | string;
  tsh?: number | string;
  colesterolTotal?: number | string;
  triglicerides?: number | string;
  hdl?: number | string;
  ldl?: number | string;
  nivelEverolimus?: number | string;
  nivelSirolimus?: number | string;
}

export interface SorologiaEntrada {
  pacienteId: string;
  dataColeta?: string;
  hbsag?: string;
  antiHBs?: string;
  antiHBcIgG?: string;
  antiHCV?: string;
  antiHIV?: string;
  // Sorologias ampliadas (B242 MVP3a, AI_112) — qualitativas (normalizarSorologia)
  hbeag?: string;
  antiHBe?: string;
  antiHBcIgM?: string;
  antiHAVIgG?: string;
  antiHDV?: string;
  cmvIgG?: string;
  ebvIgG?: string;
  toxoIgG?: string;
  vdrl?: string;
  chagasIgG?: string;
  htlv?: string;
  estrongiloidesIgG?: string;
  igraTB?: string;
  // Cargas virais (numéricas; ver normalizarCargaViral)
  hcvRNA?: string;
  pcrVHB?: string; // HBV-DNA
  cmvPCR?: string;
}

export interface MarcadorEntrada {
  pacienteId: string;
  dataColeta?: string;
  // Autoanticorpos (título/padrão)
  fan?: string;
  antiMusculoLiso?: string;
  antiMitocondria?: string;
  antiLKM?: string;
  anca?: string;
  // Marcadores tumorais / etiológicos (numéricos como string; B242 MVP3a, AI_112)
  afp?: string;
  ca199?: string;
  cea?: string;
  ferritina?: string;
  ferro?: string;
  saturacaoTransferrina?: string;
  ceruloplasmina?: string;
  alfa1Antitripsina?: string;
  clearanceCreatinina?: string;
  clearanceCreatininaCorrigido?: string;
  proteinuria24h?: string;
  sodioUrinarioAmostra?: string;
  sodioUrinario24h?: string;
  iggSerico?: string;
  igg4?: string;
}

// ── Coerções tolerantes (sempre devolvem valor "limpo" ou null = faltante) ────

function numOuNull(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = parseFloat(v.replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function strOuNull(v: unknown): string | null {
  if (typeof v === "string") {
    const t = v.trim();
    return t === "" ? null : t;
  }
  return null;
}

function boolOuNull(v: unknown): boolean | null {
  if (typeof v === "boolean") return v;
  return null;
}

function norm(v: unknown): string {
  return String(v ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();
}

// ── Derivações ────────────────────────────────────────────────────────────────

function calcularIdade(dataNascimento: string, hoje: Date): number | null {
  const iso = normalizarDataParaISO(dataNascimento);
  if (!iso) return null;
  const [ano, mes, dia] = iso.split("-").map(Number);
  if (!ano || !mes || !dia) return null;
  let idade = hoje.getFullYear() - ano;
  const m = hoje.getMonth() + 1; // getMonth() é 0-based
  if (m < mes || (m === mes && hoje.getDate() < dia)) idade--;
  return idade >= 0 && idade < 130 ? idade : null;
}

function diasEntre(isoInicio: string, hoje: Date): number | null {
  if (!isoInicio) return null;
  const [ano, mes, dia] = isoInicio.split("-").map(Number);
  if (!ano || !mes || !dia) return null;
  const inicio = new Date(ano, mes - 1, dia);
  inicio.setHours(0, 0, 0, 0);
  const fim = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  const ms = fim.getTime() - inicio.getTime();
  const dias = Math.floor(ms / 86400000);
  return dias >= 0 ? dias : null;
}

// Getters com fallback (inscricaoTx legado → campos planos) — ESPELHO de
// DashboardStats, a fonte única da classificação pré/lista/pós no app.
function getNumeroRGCT(p: PacienteEntrada): string | undefined {
  return p.inscricaoTx?.numeroRGCT || p.numeroRGCT || p.rgct;
}
function getDataTransplante(p: PacienteEntrada): string | undefined {
  return p.inscricaoTx?.dataTransplante || p.dataTransplante;
}
function temValor(v: unknown): boolean {
  return v !== undefined && v !== null && String(v).trim() !== "";
}

// Classificação pré/lista/pós IDÊNTICA ao Dashboard. NÃO usa o campo
// `statusTransplante` (nasce vazio no cadastro e quase nunca é mantido) — usa a
// MESMA regra estrutural das outras telas, para a pesquisa não divergir:
//   pós   = tem data de transplante
//   lista = tem número RGCT (inscrito) e ainda não transplantou
//   pré   = não tem RGCT nem data de transplante
function derivarStatusTransplante(p: PacienteEntrada): string {
  if (normalizarDataParaISO(getDataTransplante(p))) return "pos";
  if (temValor(getNumeroRGCT(p))) return "lista";
  return "pre";
}

// Grupo / linha de cuidado a partir de `tipoAtendimento`. Pâncreas e rim-pâncreas
// caem no MESMO grupo "pancreas" (decisão de Leonardo, AI_111). Valor desconhecido
// → null (faltante), para não forçar classificação errada.
function derivarGrupo(p: PacienteEntrada): string | null {
  const t = norm(p.tipoAtendimento);
  if (t === "figado") return "figado";
  if (t === "rim") return "rim";
  if (t === "pancreas" || t === "rim_pancreas") return "pancreas";
  if (t === "ambulatorial") return "ambulatorial";
  // B351 (AI_152) — sem este ramo o doador caía em `null` e a aba Pesquisa o
  // contava como DADO FALTANTE na linha de cuidado, em vez de como uma coorte.
  if (t === "doador_figado") return "doador_figado";
  return null;
}

// CHC como flag de pesquisa: derivada do tipo de situação especial OU da causa
// da cirrose mencionando carcinoma hepatocelular. É um sinal DERIVADO calculável
// para todos → sempre booleano (nunca faltante).
function derivarCHC(p: PacienteEntrada): boolean {
  // Fonte da verdade (B248/AI_111): a marcação estruturada da Avaliação Clínica.
  // NÃO depende de situação especial (HCC avançado/não listado nunca teria SE).
  if (p.avaliacaoClinica?.hepatocarcinoma === true) return true;
  if (p.avaliacaoClinica?.hepatocarcinoma === false) return false;
  // Reforço só quando a marcação não foi preenchida: SE/causa mencionando CHC.
  const alvo = `${norm(p.tipoSE)} ${norm(p.inscricaoTx?.tipoSE)} ${norm(
    p.avaliacaoClinica?.causaCirrose
  )}`;
  return (
    alvo.includes("carcinoma hepatocelular") ||
    alvo.includes("hepatocarcinoma") ||
    /\bchc\b/.test(alvo)
  );
}

// Classe Child-Pugh (A/B/C) a partir de string "B (8pts)" OU de pontos numéricos.
export function classeChildPugh(v: unknown): string | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "string") {
    // Remove o rótulo "CLASSE" para não confundir "Classe A" com a letra C.
    const t = v.trim().toUpperCase().replace(/CLASSE/g, "").trim();
    if (t.startsWith("A")) return "A";
    if (t.startsWith("B")) return "B";
    if (t.startsWith("C")) return "C";
    // pode ser pontos como string ("8")
    const n = parseFloat(t.replace(",", "."));
    if (Number.isFinite(n)) return classePorPontos(n);
    return null;
  }
  if (typeof v === "number") return classePorPontos(v);
  return null;
}

// MELD 3.0 calculado AO VIVO dos labs do exame (B248/AI_111) — igual à tabela de
// exames. Resolve o alto "faltante" do MELD na pesquisa (exames importados não
// tinham meld gravado). null se faltar algum dos 5 insumos.
function meldDoExame(
  exame: ExameEntrada | null | undefined,
  sexo: string | undefined
): number | null {
  if (!exame) return null;
  const c = numOuNull(exame.creatinina);
  const b = numOuNull(exame.bilirrubinaTotal);
  const i = numOuNull(exame.inr);
  const s = numOuNull(exame.sodio);
  const a = numOuNull(exame.albumina);
  if (c === null || b === null || i === null || s === null || a === null) return null;
  return calcularMELD({
    creatinina: c,
    bilirrubina: b,
    inr: i,
    sodio: s,
    albumina: a,
    sexoFeminino: String(sexo || "").toUpperCase() === "F",
  }).score;
}

function classePorPontos(pontos: number): string | null {
  if (!Number.isFinite(pontos) || pontos < 5) return null;
  if (pontos <= 6) return "A";
  if (pontos <= 9) return "B";
  return "C";
}

// ── Seleção por recência ──────────────────────────────────────────────────────

function agruparPorPaciente<T extends { pacienteId: string }>(
  itens: T[]
): Map<string, T[]> {
  const m = new Map<string, T[]>();
  for (const it of itens) {
    if (!it || !it.pacienteId) continue;
    const arr = m.get(it.pacienteId);
    if (arr) arr.push(it);
    else m.set(it.pacienteId, [it]);
  }
  return m;
}

// Ordena por data (ISO) desc e devolve o mais recente que satisfaz `aceita`.
function maisRecente<T>(
  itens: T[] | undefined,
  dataDe: (t: T) => string,
  aceita: (t: T) => boolean = () => true
): T | null {
  if (!itens || itens.length === 0) return null;
  let melhor: T | null = null;
  let melhorData = "";
  for (const it of itens) {
    if (!aceita(it)) continue;
    const d = normalizarDataParaISO(dataDe(it));
    // ISO "YYYY-MM-DD" ordena lexicograficamente; sem data perde para quem tem.
    if (melhor === null || d > melhorData) {
      melhor = it;
      melhorData = d;
    }
  }
  return melhor;
}

// Último valor NÃO-VAZIO de um campo entre vários registros (por recência). Útil
// para exames esparsos (autoanticorpos/cargas virais), que podem não estar no
// registro mais recente, e sim num anterior.
function ultimoValorPorCampo(
  itens: any[] | undefined,
  campo: string,
  dataDe: (it: any) => string
): any {
  if (!itens) return null;
  let melhor: any = null;
  let melhorData = "";
  for (const it of itens) {
    const v = it?.[campo];
    if (v === null || v === undefined || String(v).trim() === "") continue;
    const d = normalizarDataParaISO(dataDe(it));
    if (melhor === null || d > melhorData) {
      melhor = v;
      melhorData = d;
    }
  }
  return melhor;
}

// Carga viral → { valor: número | null, status: rótulo | null }. Separa o número
// (só dos detectáveis) do status, para a estatística não usar 0 (ver B247).
function cargaViral(raw: unknown): {
  valor: number | null;
  status: string | null;
} {
  const cv = normalizarCargaViral(raw);
  if (cv === null) return { valor: null, status: null };
  if (typeof cv === "number") return { valor: cv, status: "Detectável" };
  return { valor: null, status: cv }; // "Não detectado" | "Detectável (não quantificável)"
}

// ── Carregador principal (PURO) ────────────────────────────────────────────────

// marcadores fica por ÚLTIMO (com default) para não quebrar chamadas existentes
// que passam só (pacientes, exames, sorologias, hoje).
export function montarRegistros(
  pacientes: PacienteEntrada[],
  exames: ExameEntrada[],
  sorologias: SorologiaEntrada[],
  hoje: Date = new Date(),
  marcadores: MarcadorEntrada[] = []
): RegistroPesquisa[] {
  const examesPorPac = agruparPorPaciente(exames || []);
  const sorologiasPorPac = agruparPorPaciente(sorologias || []);
  const marcadoresPorPac = agruparPorPaciente(marcadores || []);

  return (pacientes || [])
    .filter((p) => p && p.id)
    .map((p) => {
      const examesPac = examesPorPac.get(p.id);
      const exameRecente = maisRecente(examesPac, (e) => e.data || "");
      // Para gravidade pré-Tx, o exame pré mais recente (tipoExame !== "pos").
      const examePre = maisRecente(
        examesPac,
        (e) => e.data || "",
        (e) => e.tipoExame !== "pos"
      );
      const soroPac = sorologiasPorPac.get(p.id);
      const soroRecente = maisRecente(soroPac, (s) => s.dataColeta || "");
      const marcPac = marcadoresPorPac.get(p.id);
      const dataSoro = (s: any) => s.dataColeta || "";
      const dataMarc = (m: any) => m.dataColeta || "";

      // Cargas virais (último valor não-vazio por campo; número à parte do status).
      const cvHCV = cargaViral(ultimoValorPorCampo(soroPac, "hcvRNA", dataSoro));
      const cvHBV = cargaViral(ultimoValorPorCampo(soroPac, "pcrVHB", dataSoro));
      const cvCMV = cargaViral(ultimoValorPorCampo(soroPac, "cmvPCR", dataSoro));

      // Helpers de leitura por recência (B242 MVP3a): marcador NUMÉRICO (último
      // valor não-vazio, esparso) e sorologia QUALITATIVA (idem, normalizada).
      const marcNum = (campo: string) =>
        numOuNull(ultimoValorPorCampo(marcPac, campo, dataMarc));
      const soroQual = (campo: string) =>
        normalizarSorologia(campo, ultimoValorPorCampo(soroPac, campo, dataSoro));

      const dataTxISO = normalizarDataParaISO(getDataTransplante(p));
      // HCC derivado UMA vez (booleano da pesquisa) — reusado em `chc` e na
      // etiqueta textual do `_textoBusca` (para a busca por palavra achar HCC).
      const chcDerivado = derivarCHC(p);

      const registro: RegistroPesquisa = {
        pacienteHash: gerarHashPaciente(p.id),
        // Link privado p/ a LISTA CLÍNICA identificada (gestão, médico-only): mapeia
        // o registro de volta ao paciente para exibir nome/abrir. NUNCA é
        // serializado em saída de pesquisa — export é AGREGADO (Table 1) e a IA só
        // recebe agregados/catálogo. Estatística/IA/export seguem pseudonimizados.
        _pacienteId: p.id,

        // Demografia
        idade: p.dataNascimento ? calcularIdade(p.dataNascimento, hoje) : null,
        sexo: strOuNull(p.sexo),

        // Transplante / status
        grupo: derivarGrupo(p),
        statusTransplante: derivarStatusTransplante(p),
        // Fase de alto nível (pré total × pós) — "pré total" = inscritos + não inscritos.
        faseTransplante: dataTxISO ? "pos" : "pre",
        statusPaciente: strOuNull(p.statusPaciente),
        diasPosTx: dataTxISO ? diasEntre(dataTxISO, hoje) : null,
        etiologia: strOuNull(p.avaliacaoClinica?.causaCirrose),
        chc: chcDerivado,
        hccBCLC: strOuNull(p.avaliacaoClinica?.hccBCLC),
        hccMilao: strOuNull(p.avaliacaoClinica?.hccMilao),
        comorbidades: strOuNull(p.comorbidades),

        // ── Doador vivo de fígado (B351, AI_152) ──
        // Nulos em quem não é doador: a aba Pesquisa trata null como faltante e
        // não polui a coorte de receptores. GRWR/remanescente/IMC vêm da MESMA
        // função da tela (volumetriaDoador), para não haver duas contas.
        doadorEnxerto: strOuNull(p.avaliacaoDoador?.loboDoado),
        doadorGRWR: calcularGRWR(
          Number(p.avaliacaoDoador?.volumeEnxerto ?? p.avaliacaoDoador?.pesoEnxerto),
          Number(p.avaliacaoDoador?.pesoReceptor)
        ),
        doadorRemanescente: calcularRemanescente(
          Number(
            p.avaliacaoDoador?.volumeFigadoTotal ?? p.avaliacaoDoador?.pesoFigadoTotal
          ),
          Number(p.avaliacaoDoador?.volumeEnxerto ?? p.avaliacaoDoador?.pesoEnxerto)
        ),
        doadorIMC: calcularIMC(
          Number(p.avaliacaoDoador?.peso),
          Number(p.avaliacaoDoador?.altura)
        ),
        doadorEsteatose: numOuNull(p.avaliacaoDoador?.esteatosePercentual),
        doadorVariacaoArterial: strOuNull(p.avaliacaoDoador?.variacaoArterial),
        doadorVariacaoPortal: strOuNull(p.avaliacaoDoador?.variacaoPortal),
        doadorVariacaoBiliar: strOuNull(p.avaliacaoDoador?.variacaoBiliar),
        doadorVeiasHepaticas: strOuNull(p.avaliacaoDoador?.veiasHepaticas),
        doadorIncluiVHM: strOuNull(p.avaliacaoDoador?.incluiVeiaHepaticaMedia),
        doadorPsicossocial: strOuNull(p.avaliacaoDoador?.psicossocialParecer),
        // Booleano só para quem É doador; nos demais fica null (faltante), senão
        // metade da coorte apareceria como "sem receptor vinculado".
        doadorReceptorVinculado: p.avaliacaoDoador
          ? Boolean(p.avaliacaoDoador?.receptorId)
          : null,
        // B242 MVP4 (busca textual, versão segura) — "resumo clínico pesquisável":
        // concatena os TEXTOS já expostos (etiologia + comorbidades + tipo de SE) E
        // uma etiqueta textual do PERFIL ESTRUTURADO (HCC/BCLC/Milão + condições de
        // descompensação positivas). Assim procurar "hcc", "ascite", "varizes",
        // "hepatopulmonar"… encontra o paciente pelo termo, sem precisar do filtro
        // estruturado. NÃO inclui narrativas identificadas (HMA/evoluções/laudos) —
        // essa é a "versão ampla", decisão de privacidade à parte. " | " separa campos.
        _textoBusca: (() => {
          const av = p.avaliacaoClinica || {};
          const condicoes: string[] = [];
          const add = (cond: boolean | undefined, termos: string) => {
            if (cond === true) condicoes.push(termos);
          };
          add(av.ascite, "ascite");
          add(av.encefalopatia, "encefalopatia");
          add(av.varizesEsofago, "varizes de esofago varizes esofagicas");
          add(av.varizesGastrica, "varizes gastricas");
          add(av.hdaPrevia, "HDA hemorragia digestiva alta");
          add(av.pbe, "PBE peritonite bacteriana espontanea");
          add(
            av.empiemaBacterianoEspontaneo,
            "empiema pleural bacteriano espontaneo"
          );
          add(
            av.colangiteRepeticao,
            "colangite de repeticao colangite bacteriana recorrente"
          );
          add(
            av.colangioTransparietoHepatica,
            "colangiografia drenagem transparieto hepatica CTPH"
          );
          add(av.shr, "sindrome hepatorrenal");
          add(av.dialise, "dialise hemodialise terapia de substituicao renal");
          add(av.tips, "TIPS shunt portossistemico transjugular");
          add(av.aclf, "ACLF agudizacao sobre cronica acute on chronic");
          add(av.hidrotorax, "hidrotorax hepatico derrame pleural");
          add(
            av.hipertensaoPortopulmonar,
            "hipertensao portopulmonar HPoP"
          );
          add(av.shdHepatopulmonar, "sindrome hepatopulmonar");
          add(av.pruridoIntratavel, "prurido intratavel prurido");
          add(
            av.insuficienciaHepaticaAguda,
            "insuficiencia hepatica aguda falencia hepatica aguda"
          );
          add(av.sarcopenia, "sarcopenia");
          add(
            av.hccTerapiaLocorregional,
            "terapia previa do chc TACE ablacao radioembolizacao terapia sistemica sorafenibe imunoterapia"
          );
          const partes = [
            av.causaCirrose,
            p.comorbidades,
            p.tipoSE || p.inscricaoTx?.tipoSE,
            chcDerivado ? "hepatocarcinoma hcc chc carcinoma hepatocelular" : "",
            av.hccBCLC ? `BCLC ${av.hccBCLC}` : "",
            av.hccMilao ? `${av.hccMilao} de Milao` : "",
            av.hccTerapiaIntencao ? `intencao ${av.hccTerapiaIntencao}` : "",
            condicoes.join(" "),
          ]
            .map((x) => (typeof x === "string" ? x.trim() : ""))
            .filter((x) => x !== "");
          return partes.length > 0 ? partes.join(" | ") : null;
        })(),
        tipoSanguineo: strOuNull(p.tipoSanguineo),
        situacaoEspecial: boolOuNull(
          p.situacaoEspecial ?? p.inscricaoTx?.situacaoEspecial
        ),

        // Gravidade (pré-Tx). MELD: calcula ao vivo dos labs do exame pré (mais
        // completo/atual); cai p/ scores/inscrição/meld gravado se faltar insumo.
        meld:
          meldDoExame(examePre, p.sexo) ??
          numOuNull(p.scores?.meld) ??
          numOuNull(p.inscricaoTx?.meld) ??
          numOuNull(examePre?.meld),
        meldNa:
          numOuNull(p.scores?.meldNa) ?? numOuNull(p.inscricaoTx?.meldNa),
        childPugh:
          classeChildPugh(p.scores?.childPugh) ??
          classeChildPugh(p.inscricaoTx?.childPugh) ??
          classeChildPugh(examePre?.childPugh),
        ascite: boolOuNull(p.avaliacaoClinica?.ascite),
        encefalopatia: boolOuNull(p.avaliacaoClinica?.encefalopatia),

        // Hipertensão portal / descompensação (B242 MVP3a, AI_112)
        varizesEsofago: boolOuNull(p.avaliacaoClinica?.varizesEsofago),
        varizesGastrica: boolOuNull(p.avaliacaoClinica?.varizesGastrica),
        hdaPrevia: boolOuNull(p.avaliacaoClinica?.hdaPrevia),
        pbe: boolOuNull(p.avaliacaoClinica?.pbe),
        empiemaBacterianoEspontaneo: boolOuNull(
          p.avaliacaoClinica?.empiemaBacterianoEspontaneo
        ),
        colangiteRepeticao: boolOuNull(
          p.avaliacaoClinica?.colangiteRepeticao
        ),
        colangioTransparietoHepatica: boolOuNull(
          p.avaliacaoClinica?.colangioTransparietoHepatica
        ),
        shr: boolOuNull(p.avaliacaoClinica?.shr),
        hidrotorax: boolOuNull(p.avaliacaoClinica?.hidrotorax),
        hipertensaoPortopulmonar: boolOuNull(
          p.avaliacaoClinica?.hipertensaoPortopulmonar
        ),
        shdHepatopulmonar: boolOuNull(p.avaliacaoClinica?.shdHepatopulmonar),
        pruridoIntratavel: boolOuNull(p.avaliacaoClinica?.pruridoIntratavel),
        insuficienciaHepaticaAguda: boolOuNull(
          p.avaliacaoClinica?.insuficienciaHepaticaAguda
        ),
        aclf: boolOuNull(p.avaliacaoClinica?.aclf),
        tips: boolOuNull(p.avaliacaoClinica?.tips),
        dialise: boolOuNull(p.avaliacaoClinica?.dialise),
        sarcopenia: boolOuNull(p.avaliacaoClinica?.sarcopenia),
        paracentese: boolOuNull(p.avaliacaoClinica?.paracentese),
        ligaduraElastica: boolOuNull(p.avaliacaoClinica?.ligaduraElastica),
        hccTerapiaLocorregional: boolOuNull(
          p.avaliacaoClinica?.hccTerapiaLocorregional
        ),
        hccTerapiaIntencao: strOuNull(
          p.avaliacaoClinica?.hccTerapiaIntencao
        ),

        // Laboratório (coleta mais recente)
        hemoglobina: numOuNull(exameRecente?.hemoglobina),
        plaquetas: numOuNull(
          exameRecente
            ? normalizarContagemMilhar(exameRecente.plaquetas, 1000)
            : null
        ),
        creatinina: numOuNull(exameRecente?.creatinina),
        bilirrubinaTotal: numOuNull(exameRecente?.bilirrubinaTotal),
        albumina: numOuNull(exameRecente?.albumina),
        inr: numOuNull(exameRecente?.inr),
        // Laboratório ampliado (B242 MVP3a, AI_112)
        tgo: numOuNull(exameRecente?.tgo),
        tgp: numOuNull(exameRecente?.tgp),
        ggt: numOuNull(exameRecente?.ggt),
        fosfataseAlcalina: numOuNull(exameRecente?.fosfataseAlcalina),
        bilirrubinaDireta: numOuNull(exameRecente?.bilirrubinaDireta),
        leucocitos: numOuNull(exameRecente?.leucocitos),
        sodio: numOuNull(exameRecente?.sodio),
        potassio: numOuNull(exameRecente?.potassio),
        calcio: numOuNull(exameRecente?.calcio),
        ureia: numOuNull(exameRecente?.ureia),
        glicemia: numOuNull(exameRecente?.glicemia),
        hemoglobinaGlicada: numOuNull(exameRecente?.hemoglobinaGlicada),
        pcr: numOuNull(exameRecente?.pcr),
        amilase: numOuNull(exameRecente?.amilase),
        lipase: numOuNull(exameRecente?.lipase),
        tsh: numOuNull(exameRecente?.tsh),
        colesterolTotal: numOuNull(exameRecente?.colesterolTotal),
        triglicerides: numOuNull(exameRecente?.triglicerides),
        hdl: numOuNull(exameRecente?.hdl),
        ldl: numOuNull(exameRecente?.ldl),
        tacrolimus:
          numOuNull(exameRecente?.nivelTacrolimus) ??
          numOuNull(exameRecente?.tacrolimus),
        everolimus: numOuNull(exameRecente?.nivelEverolimus),
        sirolimus: numOuNull(exameRecente?.nivelSirolimus),

        // Marcadores tumorais / etiológicos (último valor por campo; numérico)
        afp: marcNum("afp"),
        ca199: marcNum("ca199"),
        cea: marcNum("cea"),
        ferritina: marcNum("ferritina"),
        ferro: marcNum("ferro"),
        saturacaoTransferrina: marcNum("saturacaoTransferrina"),
        ceruloplasmina: marcNum("ceruloplasmina"),
        alfa1Antitripsina: marcNum("alfa1Antitripsina"),
        clearanceCreatinina: marcNum("clearanceCreatinina"),
        clearanceCreatininaCorrigido: marcNum("clearanceCreatininaCorrigido"),
        proteinuria24h: marcNum("proteinuria24h"),
        sodioUrinarioAmostra: marcNum("sodioUrinarioAmostra"),
        sodioUrinario24h: marcNum("sodioUrinario24h"),
        iggSerico: marcNum("iggSerico"),
        igg4: marcNum("igg4"),

        // Sorologias (status mais recente) — normalizadas na leitura (B247): até
        // registros antigos ("Positivo") entram padronizados ("Reagente") na coorte.
        hbsag: normalizarSorologia("hbsag", soroRecente?.hbsag),
        antiHBs: normalizarSorologia("antiHBs", soroRecente?.antiHBs),
        antiHBcIgG: normalizarSorologia("antiHBcIgG", soroRecente?.antiHBcIgG),
        antiHCV: normalizarSorologia("antiHCV", soroRecente?.antiHCV),
        antiHIV: normalizarSorologia("antiHIV", soroRecente?.antiHIV),
        // Sorologias ampliadas (B242 MVP3a, AI_112) — último valor não-vazio por campo
        hbeag: soroQual("hbeag"),
        antiHBe: soroQual("antiHBe"),
        antiHBcIgM: soroQual("antiHBcIgM"),
        antiHAVIgG: soroQual("antiHAVIgG"),
        antiHDV: soroQual("antiHDV"),
        cmvIgG: soroQual("cmvIgG"),
        ebvIgG: soroQual("ebvIgG"),
        toxoIgG: soroQual("toxoIgG"),
        vdrl: soroQual("vdrl"),
        chagasIgG: soroQual("chagasIgG"),
        htlv: soroQual("htlv"),
        estrongiloidesIgG: soroQual("estrongiloidesIgG"),
        igraTB: soroQual("igraTB"),

        // Autoanticorpos (último valor por campo; título/padrão padronizados B247)
        fan: normalizarAutoanticorpo("fan", ultimoValorPorCampo(marcPac, "fan", dataMarc)),
        antiMusculoLiso: normalizarAutoanticorpo(
          "antiMusculoLiso",
          ultimoValorPorCampo(marcPac, "antiMusculoLiso", dataMarc)
        ),
        antiMitocondria: normalizarAutoanticorpo(
          "antiMitocondria",
          ultimoValorPorCampo(marcPac, "antiMitocondria", dataMarc)
        ),
        antiLKM: normalizarAutoanticorpo("antiLKM", ultimoValorPorCampo(marcPac, "antiLKM", dataMarc)),
        anca: normalizarAutoanticorpo("anca", ultimoValorPorCampo(marcPac, "anca", dataMarc)),

        // Cargas virais (número só dos detectáveis + status; nunca 0)
        hcvRNAValor: cvHCV.valor,
        hcvRNAStatus: cvHCV.status,
        hbvDNAValor: cvHBV.valor,
        hbvDNAStatus: cvHBV.status,
        cmvPCRValor: cvCMV.valor,
        cmvPCRStatus: cvCMV.status,
      };

      return registro;
    });
}
